import { getAPI } from "./apiMethods";
import {
  FILESYSTEM_STRUCTURE_ENDPOINT,
  FILESYSTEM_FOLDERS_ENDPOINT,
  FILESYSTEM_FILES_ENDPOINT,
  FILESYSTEM_ITEMS_ENDPOINT,
  FILESYSTEM_ITEM_BY_ID_ENDPOINT,
  FILESYSTEM_ITEM_RENAME_ENDPOINT,
  FILESYSTEM_ITEM_MOVE_ENDPOINT,
  FILESYSTEM_FOLDER_BY_ID_ENDPOINT,
  FILESYSTEM_SEARCH_ENDPOINT,
  FILESYSTEM_FILE_DOWNLOAD_ENDPOINT,
} from "./endpoints";
import { FILESYSTEM_MY_LIBRARY_ENDPOINT } from "./endpoints";

const API_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api";

function getAuthHeaders() {
  const token = localStorage.getItem("jwtToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Get the complete file structure
const getFileStructure = async () => {
  try {
    const response = await fetch(`${API_URL}/filesystem/structure`, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch file structure");
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching file structure:", error);
    throw error;
  }
};

// Create a new folder
const createFolder = async (name, parentId = null) => {
  try {
    const response = await fetch(`${API_URL}/filesystem/folders`, {
      method: "POST",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ name, parentId }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to create folder");
    }

    return data;
  } catch (error) {
    console.error("Error creating folder:", error);
    throw error;
  }
};

// Upload a file
const uploadFile = async (file, parentId = null) => {
  try {
    const formData = new FormData();
    formData.append("file", file);
    if (parentId) {
      formData.append("parentId", parentId);
    }

    const response = await fetch(`${API_URL}/filesystem/files`, {
      method: "POST",
      headers: {
        ...getAuthHeaders(),
      },
      body: formData,
    });

    const result = await response.json();

    if (!response.ok) {
      throw new Error(result.message || "Failed to upload file");
    }

    if (!result.success) {
      throw new Error(result.message || "Upload failed");
    }

    return result.data;
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

// Delete an item (file or folder)
const deleteItem = async (id) => {
  try {
    const response = await fetch(`${API_URL}/filesystem/items/${id}`, {
      method: "DELETE",
      headers: {
        ...getAuthHeaders(),
      },
    });

    if (!response.ok) {
      throw new Error("Failed to delete item");
    }

    return await response.json();
  } catch (error) {
    console.error("Error deleting item:", error);
    throw error;
  }
};

// Rename an item
const renameItem = async (id, newName) => {
  try {
    const response = await fetch(`${API_URL}/filesystem/items/${id}/rename`, {
      method: "PATCH",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ newName }),
    });

    if (!response.ok) {
      throw new Error("Failed to rename item");
    }

    return await response.json();
  } catch (error) {
    console.error("Error renaming item:", error);
    throw error;
  }
};

// Move an item
const moveItem = async (id, newParentId) => {
  try {
    const response = await fetch(`${API_URL}/filesystem/items/${id}/move`, {
      method: "PATCH",
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ newParentId }),
    });

    if (!response.ok) {
      throw new Error("Failed to move item");
    }

    return await response.json();
  } catch (error) {
    console.error("Error moving item:", error);
    throw error;
  }
};

// Get folder contents
const getFolderContents = async (folderId) => {
  try {
    const response = await fetch(`${API_URL}/filesystem/folders/${folderId}`, {
      headers: {
        ...getAuthHeaders(),
      },
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to get folder contents");
    }
    return data;
  } catch (error) {
    throw new Error(error.message || "Failed to get folder contents");
  }
};

// Search items
const searchItems = async (query) => {
  try {
    const response = await fetch(
      `${API_URL}/filesystem/search?query=${encodeURIComponent(query)}`,
      {
        headers: {
          ...getAuthHeaders(),
        },
      }
    );
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.message || "Failed to search items");
    }
    return data;
  } catch (error) {
    throw new Error(error.message || "Failed to search items");
  }
};

// Get file download info
const getFileDownload = async (fileId, disposition = "attachment") => {
  try {
    const response = await fetch(
      `${API_URL}/filesystem/files/${fileId}/download?disposition=${disposition}`,
      {
        headers: {
          ...getAuthHeaders(),
          Accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to get download URL");
    }

    const data = await response.json();

    // Validate the response structure
    if (!data.data?.download?.url) {
      throw new Error("Invalid download URL response from server");
    }

    return data;
  } catch (error) {
    console.error("Error getting file download:", error);
    throw error;
  }
};

// Trigger actual file download
const downloadFile = async (fileId) => {
  try {
    const downloadData = await getFileDownload(fileId, "attachment");

    const downloadUrl = downloadData.data.download.url;
    const fileName = downloadData.data.file?.name || "download";

    if (!downloadUrl) {
      throw new Error("No download URL provided by server");
    }

    // Fetch the file as a blob and trigger a real download so it
    // doesn't just open in a new tab for inline-capable types.
    const response = await fetch(downloadUrl, { credentials: "omit" });
    if (!response.ok) {
      throw new Error("Failed to fetch file for download");
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(objectUrl);

    return downloadData;
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

export {
  getFileStructure,
  createFolder,
  uploadFile,
  deleteItem,
  renameItem,
  moveItem,
  getFolderContents,
  searchItems,
  getFileDownload,
  downloadFile,
};

// Fetch current user's library folder
export const getMyLibraryFolder = async () => {
  try {
    const resp = await fetch(`${API_URL}${FILESYSTEM_MY_LIBRARY_ENDPOINT}`, {
      headers: {
        ...getAuthHeaders(),
        "Content-Type": "application/json",
      },
    });
    const data = await resp.json();
    if (!resp.ok) {
      throw new Error(data.message || "Failed to fetch my library folder");
    }
    return data.data?.folder || null;
  } catch (e) {
    console.error("Error fetching my library folder:", e);
    return null;
  }
};
